import { Box, Checkbox, FormControlLabel, Link, Typography, useTheme } from '@mui/material';
import { type JSX } from 'react';
import { useLocale } from '@repo/i18n';
import { Iso2Code } from '@repo/types';
import Button from 'src/components/common/timeslots-buttons/button/Button';
import CountrySelect from 'src/components/common/country-select/CountrySelect';
import TextInput from 'src/components/common/timeslots-fields/inputs/text-input/TextInput';
import NativePasswordInput from './NativePasswordInput';
import type { RegisterFieldErrors } from './nativeAuthValidation';

type NativeRegisterFormProps = {
    firstName: string;
    setFirstName: (v: string) => void;
    lastName: string;
    setLastName: (v: string) => void;
    email: string;
    setEmail: (v: string) => void;
    password: string;
    setPassword: (v: string) => void;
    passwordConfirm: string;
    setPasswordConfirm: (v: string) => void;
    phone: string;
    setPhone: (v: string) => void;
    country: Iso2Code;
    setCountry: (v: Iso2Code) => void;
    acceptTerms: boolean;
    setAcceptTerms: (v: boolean) => void;
    errors: RegisterFieldErrors;
    emailExists: boolean;
    onSwitchToLogin: () => void;
    termsUrl: string;
    privacyUrl: string;
    canSubmit: boolean;
    submit: () => void;
};

// Dumb sign-up form. All values, handlers, errors and the terms/privacy URLs come from props
// (the smart layer is useNativeAuth); only styling/i18n hooks are used here.
export default function NativeRegisterForm(props: NativeRegisterFormProps): JSX.Element {
    const { t } = useLocale();
    const theme = useTheme();
    const {
        firstName,
        setFirstName,
        lastName,
        setLastName,
        email,
        setEmail,
        password,
        setPassword,
        passwordConfirm,
        setPasswordConfirm,
        phone,
        setPhone,
        country,
        setCountry,
        acceptTerms,
        setAcceptTerms,
        errors,
        emailExists,
        onSwitchToLogin,
        termsUrl,
        privacyUrl,
        canSubmit,
        submit,
    } = props;

    return (
        <Box
            sx={{
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(2),
            }}
        >
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing(2) }}>
                <TextInput
                    label={t.first_name}
                    value={firstName}
                    onChange={setFirstName}
                    autoComplete="given-name"
                    required
                />
                <TextInput
                    label={t.last_name}
                    value={lastName}
                    onChange={setLastName}
                    autoComplete="family-name"
                    required
                />
            </Box>

            <TextInput
                label={t.email}
                value={email}
                onChange={setEmail}
                autoComplete="email"
                error={Boolean(errors.email)}
                helperText={errors.email}
                required
            />
            {emailExists && (
                <Link
                    component="button"
                    type="button"
                    onClick={onSwitchToLogin}
                    sx={{ alignSelf: 'flex-start', color: theme.palette.primary.main }}
                >
                    {t.login}
                </Link>
            )}

            <NativePasswordInput
                label={t.password}
                value={password}
                onChange={setPassword}
                autoComplete="new-password"
                error={Boolean(errors.password)}
                required
            />
            <NativePasswordInput
                label={t.confirm_password}
                value={passwordConfirm}
                onChange={setPasswordConfirm}
                autoComplete="new-password"
                error={Boolean(errors.password)}
                helperText={errors.password}
                required
            />

            <TextInput
                label={t.phone_number}
                value={phone}
                onChange={setPhone}
                autoComplete="tel"
                error={Boolean(errors.phone)}
                helperText={errors.phone}
            />

            <CountrySelect country={country} onChange={setCountry} />

            <FormControlLabel
                control={
                    <Checkbox
                        checked={acceptTerms}
                        onChange={(e) => setAcceptTerms(e.target.checked)}
                    />
                }
                label={
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        {t.i_have_read_and_accepted_the}{' '}
                        <Link
                            href={termsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            color="primary"
                        >
                            {t.terms_and_conditions}
                        </Link>{' '}
                        {t.and}{' '}
                        <Link
                            href={privacyUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            color="primary"
                        >
                            {t.privacy_policy}
                        </Link>
                    </Typography>
                }
            />

            {errors.general && (
                <Typography role="alert" sx={{ color: theme.palette.error.main }}>
                    {errors.general}
                </Typography>
            )}

            <Button text={t.create_account} onClick={() => submit()} disabled={!canSubmit} />
        </Box>
    );
}
